import {noop} from '@benchling/js-utils';
import {Stack, Text, TextInput} from '@mantine/core';
import assert from 'assert';
import {produce} from 'immer';
import React, {useEffect} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';

import ApplyButton from '../annotate-fill-plate-modal/ApplyButton';
import {
  useConcentrationLayerActorRef,
  useConcentrationLayerSelector,
} from '../concentration-layers/ConcentrationLayerMachineProvider';
import {
  ConcentrationLayerData,
  ConcentrationSettings,
  DilutionSettings as DilutionSettingsType,
} from '../concentration-layers/machine/ConcentrationLayerMachine';
import {PlateMap} from '../PlateMapConstants';
import {calculateConcentrationPreviewPlateMap} from '../PlateMapUtil';
import ConcentrationSettingsComponent, {isConcentrationSettingsValid} from './ConcentrationSettings';

export interface ConcentrationSettingsProps {
  /** The active layer to configure. */
  activeLayer: ConcentrationLayerData | null;
  /** Existing concentration layer names */
  existingConcentrationLayerNames: Set<string>;
  /** Callback functions */
  onUpdateLayerName: (layerId: string, name: string) => void;
  onUpdateConcentrationSettings: (layerId: string, settings: Partial<ConcentrationSettings>) => void;
  onUpdateLayerDilutionSettings: (layerId: string, settings: Partial<DilutionSettingsType>) => void;
  onApply: () => void;
}

/**
 * Settings component for configuring concentration layers in plate maps.
 *
 * Allows users to:
 * - Set a name for the concentration layer
 * - Define concentration values and units
 * - Optionally define dilution settings including dilution factor, dilution direction, and replicates
 */
export default function ConcentrationLayerSettings({
  activeLayer,
  existingConcentrationLayerNames,
  onUpdateLayerName,
  onUpdateConcentrationSettings,
  onUpdateLayerDilutionSettings,
  onApply,
}: ConcentrationSettingsProps): JSX.Element {
  assert(activeLayer != null, 'Expected active layer to be defined.');
  const isNameUnique = !existingConcentrationLayerNames.has(activeLayer.name);
  return (
    <Stack spacing="sm" {...getTestProps({component: 'ConcentrationLayerSettings', element: 'root'})}>
      <Text size="sm" c="dimmed">
        Select wells in the plate to define concentration values.
      </Text>
      <TextInput
        size="md"
        label="Name"
        labelProps={{fw: 'bold'}}
        autoFocus={true}
        onChange={(e) => {
          onUpdateLayerName(activeLayer.layerId, e.target.value);
        }}
        placeholder="Name"
        value={activeLayer.name}
        error={!isNameUnique ? 'Concentration layer names must be unique.' : undefined}
      />
      <ConcentrationSettingsComponent
        concentrationSettings={activeLayer}
        dilutionSettings={activeLayer.dilutionSettings}
        onUpdateConcentrationSettings={(settings: Partial<ConcentrationSettings>) => {
          onUpdateConcentrationSettings(activeLayer.layerId, settings);
        }}
        onUpdateLayerDilutionSettings={(settings: Partial<DilutionSettingsType>) => {
          onUpdateLayerDilutionSettings(activeLayer.layerId, settings);
        }}
      />
      <ApplyButton disabled={!isConcentrationSettingsValid(activeLayer) || !isNameUnique} onClick={onApply} />
    </Stack>
  );
}

export interface ProvidedConcentrationLayerSettingsProps {
  /** Current plate map */
  plateMap: PlateMap;
  /** Function to update the plate map */
  onEditPlateMap: (plateMap: PlateMap, layerUsesDilution: boolean) => void;
  /** Currently selected well indices */
  selectedWellIndices: Set<number>;
  /** Function to deselect wells */
  deselectWells: () => void;
  /** Plate map with previewed values */
  previewPlateMap: PlateMap | null;
  /** Function to set plate map with previewed values */
  setPlateMapWithPreview: (plateMap: PlateMap | null) => void;
}

function ApplyConcentrationLayerConfirmationModal({
  oldUnitSymbol,
  newUnitSymbol,
  onRequestSubmit,
  onRequestClose,
}: {
  oldUnitSymbol: string;
  newUnitSymbol: string;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
}): JSX.Element {
  return (
    <Modal
      title="Change the unit for this layer?"
      onRequestSubmit={onRequestSubmit}
      onRequestClose={onRequestClose}
      medium={true}
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          submit: true,
          text: 'Yes, change unit',
        },
      ]}
    >
      <p>
        Applying these values will change the unit for the entire layer from <b>{oldUnitSymbol}</b> to{' '}
        <b>{newUnitSymbol}</b>.
      </p>
    </Modal>
  );
}

/**
 * A wrapper component that connects ConcentrationLayerSettings to the concentration layer machine context.
 * This component eliminates the need to thread concentration layer props through multiple component layers.
 */
export function ProvidedConcentrationLayerSettings({
  plateMap,
  onEditPlateMap,
  selectedWellIndices,
  deselectWells,
  previewPlateMap,
  setPlateMapWithPreview,
}: ProvidedConcentrationLayerSettingsProps): JSX.Element | null {
  const actor = useConcentrationLayerActorRef();

  const {layers, activeLayerId} = useConcentrationLayerSelector((state) => state.context);
  const activeLayer = activeLayerId ? (layers.get(activeLayerId) ?? null) : null;

  useEffect(() => {
    const concentrationLayerData = activeLayer ? layers.get(activeLayer.layerId) : null;
    let newPreviewPlateMap: PlateMap | null = null;
    if (concentrationLayerData != null && plateMap != null) {
      newPreviewPlateMap = calculateConcentrationPreviewPlateMap(
        concentrationLayerData,
        plateMap,
        selectedWellIndices
      );
    }
    setPlateMapWithPreview(newPreviewPlateMap);
  }, [activeLayer, activeLayerId, layers, selectedWellIndices, plateMap, setPlateMapWithPreview]);

  // Don't render if there's no active layer
  if (activeLayer == null) {
    return null;
  }

  /**
   * Handles the apply button click.
   *
   * If the active layer has a unit that differs from existing wells' units,
   * shows a confirmation modal. Otherwise, applies the values directly.
   */
  function handleApply(): void {
    // Get the existing unit symbol for the active layer
    const existingUnitSymbols = new Set<string>();
    for (const well of plateMap.wells) {
      const concentrationLayer = well.concentrationLayers?.[activeLayer!.layerId];
      if (concentrationLayer?.unit?.symbol) {
        existingUnitSymbols.add(concentrationLayer.unit.symbol);
      }
    }
    assert(existingUnitSymbols.size <= 1, 'Expected at most one unit symbol');
    const hasExistingUnit = existingUnitSymbols.size === 1;

    const currentUnitSymbol = activeLayer!.concentrationUnit?.symbol;
    const isUnitChanging =
      hasExistingUnit && currentUnitSymbol && !existingUnitSymbols.has(currentUnitSymbol);

    if (isUnitChanging) {
      const [oldUnitSymbol] = existingUnitSymbols;
      runModal(ApplyConcentrationLayerConfirmationModal, {
        oldUnitSymbol,
        newUnitSymbol: currentUnitSymbol,
        onRequestSubmit: applyConcentrationLayer,
        onRequestClose: noop,
      });
    } else {
      applyConcentrationLayer();
    }
  }

  function applyConcentrationLayer(): void {
    if (!previewPlateMap || !activeLayer) {
      return;
    }
    const newPlateMap = produce(previewPlateMap, (draft) => {
      draft.concentrationLayerInfoById = {
        ...draft.concentrationLayerInfoById,
        [activeLayer.layerId]: {
          layerId: activeLayer.layerId,
          name: activeLayer.name,
        },
      };
    });
    onEditPlateMap(newPlateMap, activeLayer.allowSetDilutionSettings);
    deselectWells();
  }

  // Get names of other concentration layers (excluding the current active layer)
  const existingConcentrationLayerNames: Set<string> = plateMap.concentrationLayerInfoById
    ? new Set(
        Object.entries(plateMap.concentrationLayerInfoById)
          .filter(([layerId]) => layerId !== activeLayer.layerId)
          .map(([, info]) => info.name)
      )
    : new Set();

  return (
    <ConcentrationLayerSettings
      activeLayer={activeLayer}
      existingConcentrationLayerNames={existingConcentrationLayerNames}
      onUpdateLayerName={(layerId: string, name: string) => {
        actor.send({
          type: 'updateLayerName',
          layerId,
          name,
        });
      }}
      onUpdateConcentrationSettings={(layerId: string, settings: Partial<ConcentrationSettings>) => {
        actor.send({
          type: 'updateConcentrationSettings',
          layerId,
          settings,
        });
      }}
      onUpdateLayerDilutionSettings={(layerId: string, settings: Partial<DilutionSettingsType>) => {
        actor.send({
          type: 'updateLayerDilutionSettings',
          layerId,
          settings,
        });
      }}
      onApply={handleApply}
    />
  );
}
